import LeadBudget from './steps/LeadBudget'
import LeadPayer from './steps/LeadPayer'
import LeadRecurrence from './steps/LeadRecurrence'
import LeadEvent from './steps/LeadEvent'
import LeadLocation from './steps/LeadLocation'
import LeadDate from './steps/LeadDate'
import LeadEventDate from './steps/LeadEventDate'
import LeadOccasion from './steps/LeadOccasion'
import LeadCateringOccasion from './steps/LeadCateringOccasion'
import LeadCateringWedding from './steps/LeadCateringWedding'
import LeadEaters from './steps/LeadEaters'
import LeadFoods from './steps/LeadFoods'
import LeadFoodTypes from './steps/LeadFoodTypes'
import LeadMinGuarantee from './steps/LeadMinGuarantee'
import LeadComment from './steps/LeadComment'
import LeadContact from './steps/LeadContact'
import LeadCuisines from './steps/LeadCuisines'
import LeadSuccess from './LeadSuccess'
import LeadFail from './LeadFail'
import LeadLoading from './LeadLoading'
import { set as setTime } from 'date-fns'
import { isValidEmail, normalizeEatersValue, validateEaters } from 'app/utils/validation'

const isPayerAndOccasionSet = data => !!data.payer && !!data.occasion?.code
const isBusinessOccasion = data => data.payer === 'host' && data.occasion?.code === 'business'
const isPayerAttendee = data => data.payer === 'attendee'
const isPayerHostOrUndefined = data => data.payer === 'host' || data.payer === undefined
const isNotBusinessOccasion = data => data.occasion?.code != 'business'
const isAPersonalOccasion = data => data.payer === 'host' && data.occasion?.code?.match(/personal|wedding/)
const isWeddingOccasion = data => data.occasion?.code?.match(/wedding/)
const isVendingControl = (data, experiments) => isPayerAttendee(data) && experiments['loco_lead_form_vending'] === 'control'
const isVendingTest = (data, experiments) => isPayerAttendee(data) && experiments['loco_lead_form_vending'] === 'test'
const isVendingTestRecurring = (data, experiments) => isVendingTest(data, experiments) && data.recurring === true
const isVendingTestOneTime = (data, experiments) => isVendingTest(data, experiments) && data.recurring === false


export const steps = [
  {
    name: 'payer',
    component: LeadPayer,
    progress: 8,
    label: 'Payer',
    heading: 'Who\'s paying?',
    validate: (data) => {
      let validations = {}
      if (!data.payer) {
        validations.payer = 'Please choose a payer'
      }
      return Object.keys(validations).length ? validations : null
    }
  },
  {
    name: 'recurrence',
    isEnabled: isPayerAttendee,
    component: LeadRecurrence,
    progress: 16,
    label: 'Recurring',
    heading: 'One-Time Event or Recurring Food Service?',
    validate: (data) => {
      let validations = {}
      if (data.recurring === undefined) {
        validations.recurrence = 'Please choose an option'
      }
      return Object.keys(validations).length ? validations : null
    }
  },
  {
    name: 'event',
    isEnabled: (data, experiments) => (isPayerAttendee(data) && !isVendingTest(data, experiments)) || isVendingTestOneTime(data, experiments),
    component: LeadEvent,
    progress: 24,
    label: 'Event Details',
    heading: 'What are your Event Details?',
    validate: (data) => {
      let validations = {}
      if (!data.event_name) {
        validations.event_name = 'This field is required'
      }
      return Object.keys(validations).length ? validations : null
    }
  },
  {
    name: 'occasion',
    isEnabled: (data, experiments) => isPayerAttendee(data) && !isVendingTest(data, experiments),
    component: LeadOccasion,
    progress: 32,
    label: 'Occasion',
    heading: 'Occasion',
    validate: (data) => {
      let validations = {}
      if (!data.occasion) {
        validations.occasion = 'Please choose an occasion for this event'
      }
      return Object.keys(validations).length ? validations : null
    }
  },
  {
    name: 'occasionCatering',
    isEnabled: isPayerHostOrUndefined,
    component: LeadCateringOccasion,
    progress: 32,
    label: 'Occasion',
    heading: 'What\'s the occasion?',
    validate: (data) => {
      let validations = {}
      if (!data.occasion) {
        validations.occasion = 'Please choose an occasion for this event'
      }
      return Object.keys(validations).length ? validations : null
    }
  },
  {
    name: 'weddingCatering',
    isEnabled: isAPersonalOccasion,
    component: LeadCateringWedding,
    progress: 32,
    label: 'Wedding',
    heading: 'Is the event a wedding?',
    validate: (data) => {
      let validations = {}
      if (!data.occasion || 
        data.occasion.code?.match(/personal|wedding/)?.length <= 0) {
        validations.occasion = 'Please choose whether this event is a wedding or not'
      }
      return Object.keys(validations).length ? validations : null
    }
  },
  {
    name: 'eaters_1',
    isEnabled: (data, experiments) => isBusinessOccasion(data) && !isVendingTestRecurring(data, experiments),
    component: LeadEaters,
    progress: 56,
    label: 'Guest Count',
    heading: 'How many guests will you have?',
    validate: (data) => {
      let validations = {}
      const normalized = normalizeEatersValue(data.eaters)
      const errorMessage = validateEaters(normalized)
      if (errorMessage) {
        validations.eaters = errorMessage
      }
      return Object.keys(validations).length ? validations : null
    }
  },
  {
    name: 'cuisine_selection',
    isEnabled: (data, experiments) => isPayerAndOccasionSet(data) && isNotBusinessOccasion(data) || isVendingControl(data, experiments),
    component: LeadCuisines,
    progress: 40,
    label: 'Cuisine Selection',
    heading: 'What type of menu options would you like to see?',
    validate: (data) => {
      let validations = {}
      // Allow validation to pass if cuisine loading failed (skip-cuisine-error case)
      const hasSkipError = data.cuisines?.includes('skip-cuisine-error')
      if (data.cuisines?.length === 0 && !hasSkipError) {
        validations.cuisines = 'Please select your cuisine preference'
      }
      return Object.keys(validations).length ? validations : null
    }
  },
  {
    name: 'foods',
    isEnabled: (data) => isPayerHostOrUndefined(data) && isNotBusinessOccasion(data),
    component: LeadFoods,
    progress: 48,
    label: 'Foods',
    heading: 'What would you like to serve?',
    validate: (data) => {
      let validations = {}
      if (!data.meals?.length) {
        validations.meals = 'Please select at least one meal.'
      }
      if (!data.foods?.length) {
        validations.foods = 'Please choose at least one dish.'
      }
      return Object.keys(validations).length ? validations : null
    }
  },
  {
    name: 'foodsBusinessOccasion',
    isEnabled: (data) => isBusinessOccasion(data),
    component: LeadFoodTypes,
    progress: 24,
    label: 'Foods',
    heading: 'What type of menu options would you like to see?',
    validate: (data) => {
      let validations = {}
      if (!data.food_types?.length) {
        validations.food_types = 'Please choose an option'
      }
      return Object.keys(validations).length ? validations : null
    }
  },
  {
    name: 'eaters_2',
    isEnabled: (data, experiments) => isNotBusinessOccasion(data) && !isVendingTestRecurring(data, experiments),
    component: LeadEaters,
    progress: 56,
    label: 'Guest Count',
    heading: 'How many guests will you have?',
    validate: (data) => {
      let validations = {}
      const normalized = normalizeEatersValue(data.eaters)
      const errorMessage = validateEaters(normalized)
      if (errorMessage) {
        validations.eaters = errorMessage
      }
      return Object.keys(validations).length ? validations : null
    }
  },

  {
    name: 'date',
    isEnabled: (data, experiments) => {
      const visibleForAttendeeLead = isPayerAttendee(data) && (!isVendingTest(data, experiments) || !data.recurring)
      const visibleForHostLead = isPayerHostOrUndefined(data) && !isBusinessOccasion(data)
      return visibleForAttendeeLead || visibleForHostLead      
    },
    component: LeadDate,
    progress: 64,
    label: 'Date',
    heading: 'When\'s your event?',
    validate: (data) => {
      let validations = {}

      const mergeTime = (day, time) => setTime(day, {
        hours: time?.getHours(),
        minutes: time?.getMinutes(),
        seconds: 0,
        milliseconds: 0,
      })
      const startTime = mergeTime(new Date(), data.start_date)
      const endTime = mergeTime(new Date(), data.end_date)

      if (!data.start_date) {
        validations.start_date = 'Please provide the event date'
      }
      else if (data.multiday && !data.end_date) {
        validations.start_date = 'Please provide the end date'
      }
      else if (
        data.multiday &&
        data.start_date.getDate() >= data.end_date.getDate() &&
        data.start_date.getMonth() >= data.end_date.getMonth() &&
        data.start_date.getYear() >= data.end_date.getYear()
      ) 
      {
        validations.start_date = 'Start date must be before end date'
      }
  
      if (data.recurring && !data.frequency) {
        validations.frequency = 'Please choose an option'
      }
      return Object.keys(validations).length ? validations : null
    }
  },
  {
    name: 'dateBusinessOccasion',
    isEnabled: (data) => isBusinessOccasion(data),
    component: LeadEventDate,
    progress: 64,
    label: 'Date',
    heading: 'When\'s your event?',
    validate: () => false
  },
  {
    name: 'location',
    isEnabled: (data, experiments) => (isPayerAndOccasionSet(data) && isNotBusinessOccasion(data)) || isVendingTestOneTime(data, experiments) || isVendingControl(data, experiments),
    component: LeadLocation,
    progress: 72,
    label: 'Location',
    heading: 'Where is your event?',
    validate: (data, country) => {
      let validations = {}

      if (!data.address?.city_name) {
        validations.city_name = 'Please provide a city'
      }

      if (!data.address?.region_code && country?.region_label) {
        validations.region_code = `Please provide a ${country.region_label || 'state'}`
      }
  
      if (!data.address?.zip && country?.zip_label) {
        validations.zip = 'Please provide the zip code'
      }
  
      return Object.keys(validations).length ? validations : null
    }
  },
  {
    name: 'budget',
    component: LeadBudget,
    isEnabled: (data, experiments) => isPayerAndOccasionSet(data) && isPayerHostOrUndefined(data) && isNotBusinessOccasion(data) && data.occasion?.name !== 'Corporate',
    progress: 80,
    label: 'Budget',
    heading: 'What\'s your budget?',
    validate: (data) => {
      let validations = {}

      if (!data.budget) {
        validations.budget = 'Please provide a budget'
      } else if (data.budget > 1000000) {
        validations.budget = 'Budget cannot be more than $1,000,000'
      } else if (data.budget < 200) {
        validations.budget = 'Please enter at least the minimum amount ($200) for your event'
      }

      return Object.keys(validations).length ? validations : null
    }
  },
  {
    name: 'min_guarantee',
    isEnabled: (data, experiments) => isPayerAttendee(data) && !isVendingTest(data, experiments),
    component: LeadMinGuarantee,
    progress: 88,
    label: 'Minimum Guarantee',
    heading: 'What\'s your guaranteed sales minimum?',
    validate: (data) => null
  },
  {
    name: 'comment',
    isEnabled: isVendingTestRecurring,
    component: LeadComment,
    progress: 90,
    label: 'Comment',
    heading: 'Final Details',
    validate: () => false
  },
  {
    name: 'contact',
    component: LeadContact,
    progress: 96,
    label: 'Contact',
    heading: 'How can we reach you?',
    validate: (data) => {
      let validations = {}
      if (!data.contact.first_name) {
        validations.first_name = 'Please enter your first name'
      }
      if (!data.contact.last_name) {
        validations.last_name = 'Please enter your last name'
      }
      if (!data.contact.email || !isValidEmail(data.contact.email)) {
        validations.email = 'Please enter a valid email address'
      }
      if (!data.contact.phone) {
        validations.phone = 'Please enter a contact number'
      }
      return Object.keys(validations).length ? validations : null
    }
  },
  {
    name: 'loading',
    component: LeadLoading,
    autoSave: false,
    hide_header: true,
    hide_footer: true,
  },
  {
    name: 'success',
    component: LeadSuccess,
    autoSave: false,
    hide_header: true,
    hide_footer: true,
  },
  {
    name: 'fail',
    component: LeadFail,
    autoSave: false,
    hide_header: true,
    hide_footer: true,
  },
]

export const postHogTrackingConfig = {
  payer: {
    event: 'Payer Information Submitted',
    event_slug: 'payer_information_submitted',
    fields: (step_index, data) => {
      return {
        payer_type: data.payer,
        step_index: step_index,
      }
    }
  },
  recurrence: {
    event: 'Vending Event Recurrence Submitted',
    event_slug: 'event_recurrence_submitted',
    fields: (step_index, data) => {
      return {
        recurrence_type: data.recurrence,
        step_index: step_index,
      }
    }
  },
  event: {
    event: 'Vending Event Details Submitted',
    event_slug: 'event_details_submitted',
    fields: (step_index, data) => {
      return {
        event_name: data.event_name,
        event_website: data.website,
        step_index: step_index,
      }
    }
  },
  min_guarantee: {
    event: 'Vending Event Minimum Guarantee Submitted',
    event_slug: 'minimum_guarantee_submitted',
    fields: (step_index, data) => {
      return {
        min_guarantee: data.min_guarantee,
        step_index: step_index,
      }
    }
  },
  location: {
    event: 'Location Information Submitted',
    event_slug: 'location_information_submitted',
    fields: (step_index, data) => {
      return {
        location_choice: data.location_status,
        step_index: step_index,
        event_city: data.address?.city_name,
        event_zipcode: data.address?.zip,
        event_state: data.address.region_name
      }
    }
  },
  date: {
    event: 'Event Date Submitted',
    event_slug: 'event_date_submitted',
    fields: (step_index, data) => {
      return {
        event_date: data.start_date,
        step_index: step_index,
      }
    }
  },
  dateBusinessOccasion: {
    event: 'Event Date Submitted',
    event_slug: 'event_date_submitted',
    fields: (step_index, data) => {
      return {
        event_date: data.start_date,
        step_index: step_index,
      }
    }
  },
  occasion: {
    event: 'Occasion Submitted',
    event_slug: 'occasion_submitted',
    fields: (step_index, data) => {
      return {
        event_occasion_name: data.occasion?.name,
        event_occasion_id: data.occasion?.id,
        step_index: step_index,
      }
    }
  },
  occasionCatering: {
    event: 'Occasion Submitted',
    event_slug: 'occasion_submitted',
    fields: (step_index, data) => {
      return {
        event_occasion_name: data.occasion?.name,
        event_occasion_id: data.occasion?.id,
        step_index: step_index,
      }
    }
  },
  weddingCatering: {
    event: 'Wedding Occasion Submitted',
    event_slug: 'wedding_occasion_submitted',
    fields: (step_index, data) => {
      return {
        event_occasion_name: data.occasion?.name,
        event_occasion_id: data.occasion?.id,
        step_index: step_index,
      }
    }
  },
  eaters_1: {
    event: 'Eaters Submitted',
    event_slug: 'eaters_submitted',
    fields: (step_index, data) => {
      return {
        event_eaters: data.eaters,
        step_index: step_index,
      }
    }
  },
  foods: {
    event: 'Meal Types Submitted',
    event_slug: 'meal_types_submitted',
    fields: (step_index, data) => {
      return {
        event_meal_types: data.foods,
        event_meals: data.meals?.join(', '),
        step_index: step_index,
      }
    }
  },
  foodsBusinessOccasion: {
    event: 'Meal Types Submitted',
    event_slug: 'meal_types_submitted',
    fields: (step_index, data) => {
      return {
        event_food_types: [...data.food_types],
        step_index: step_index,
      }
    }
  },
  eaters_2: {
    event: 'Eaters Submitted',
    event_slug: 'eaters_submitted',
    fields: (step_index, data) => {
      return {
        event_eaters: data.eaters,
        step_index: step_index,
      }
    }
  },
  cuisine_preferences: {
    event: 'Cuisine Preferences Submitted',
    event_slug: 'cuisine_preferences_submitted',
    fields: (step_index, data) => {
      return {
        event_cuisine_preferences: !!data.cuisines,
        step_index: step_index,
      }
    }
  },
  cuisine_selection: {
    event: 'Cuisine Selection Submitted',
    event_slug: 'cuisine_selection_submitted',
    fields: (step_index, data) => {
      return {
        event_cuisine_preferences: !!data.cuisines,
        event_cuisine_selection: data.cuisines,
        step_index: step_index,
      }
    }
  },
  contact: {
    event: 'Contact Information Submitted',
    event_slug: 'contact_information_submitted',
    fields: (step_index, data) => {
      return {
        step_index: step_index,
      }
    }
  },
  budget: {
    event: 'Budget Submitted',
    event_slug: 'budget_submitted',
    fields: (step_index, data) => {
      return {
        event_budget: data.budget,
        step_index: step_index,
      }
    }
  },
  timeframe: {
    event: 'Urgency Submitted',
    event_slug: 'urgency_submitted',
    fields: (step_index, data) => {
      return {
        event_urgency: data.timeframe,
        step_index: step_index,
      }
    }
  },
  comment: {
    event: 'Final Details Submitted',
    event_slug: 'final_details_submitted',
    fields: (step_index, data) => {
      return {
        event_final_details: data.comment,
        step_index: step_index,
      }
    }
  },
  success: {
    event: 'Lead Created',
    event_slug: 'lead_created',
    fields: (step_index, data) => {
      return {
        lead_id: data.lead_id,
        payer_type: data.payer,
        location_choice: data.location_status,
        event_city: data.address?.city_name,
        event_zipcode: data.address?.zip,
        event_state: data.address.region_name,
        event_date: data.start_date,
        event_occasion_name: data.occasion?.name,
        event_occasion_id: data.occasion?.id,
        event_meal_types: data.foods,
        event_meals: data.meals,
        event_cuisine_preferences: !!data.cuisines,
        event_cuisine_selection: data.cuisines,
        event_budget: data.budget,
        event_urgency: data.timeframe,
        event_final_details: data.comment
      }
    }
  }
}

const trackerStepsBase = (stepName) => ({
  // uid: cookies.get(UID_COOKIE_NAME), // Currently unavailable
  event_category: 'Lead Form',
  event_step: stepName,
})

const leadType = (payer) => {
  switch(payer) {
    case 'host':
      return "Catering"
    case 'attendee':
      return "Vending"
    default:
      return ""
  }
}

const hasComments = (data) => data.comment ? data.comment.length > 0 : false

const cuisineFields = (data) => {
  // Filter out skip-cuisine-error marker before processing
  const validCuisines = data.cuisines?.filter(cuisine => cuisine !== 'skip-cuisine-error') || []
  return {
    event_cuisines: validCuisines.length > 0 ? validCuisines.join(', ') : '',
    event_cuisine_preferences: Array.isArray(data.cuisine)
  }
}

export const trackerSteps = {
  payer: {
    getTracked: (stepName, data, nextClicked = true) => ({
      ...trackerStepsBase(stepName),
      event_payer: data.payer,
      event_payer_label: leadType(data.payer),
      event_button_clicked: nextClicked ? 'next': 'back',
    })
  },
  recurrence: {
    getTracked: (stepName, data) => ({
      ...trackerStepsBase(stepName),
      recurring: data.recurring,
    })
  },
  event: {
    getTracked: (stepName, data) => ({
      ...trackerStepsBase(stepName),
      event_name: data.event_name,
      event_website: data.website,
    })
  },
  location: {
    getTracked: (stepName, data) => ({
      ...trackerStepsBase(stepName),
      event_address_city: data.address?.city_name,
      event_address_region: data.address?.region_code,
      event_address_country: data.address?.country_code,
      event_location_status: data.location_status,
    })
  },
  date: {
    getTracked: (stepName, data) => ({
      ...trackerStepsBase(stepName),
      event_date_start: data.start_date,
      event_date_end: data.end_date,
      event_date_multiday: data.multiday,
      event_date_frequency: data.frequency,
      event_date_frequency_other: data.frequency_other,
    })
  },
  dateBusinessOccasion: {
    getTracked: (stepName, data) => ({
      ...trackerStepsBase(stepName),
      event_date_start: data.start_date,
    })
  },
  occasion: {
    getTracked: (stepName, data) => ({
      ...trackerStepsBase(stepName),
      event_occasion: data.occasion?.name,
      event_occasion_other: data.occasion_other,
    })
  },
  occasionCatering: {
    getTracked: (stepName, data) => ({
      ...trackerStepsBase(stepName),
      event_occasion: data.occasion?.name,
    })
  },
  weddingCatering: {
    getTracked: (stepName, data) => ({
      ...trackerStepsBase(stepName),
      event_occasion: data.occasion?.name,
    })
  },
  eaters_1: {
    getTracked: (stepName, data) => ({
      ...trackerStepsBase(stepName),
      event_eaters: data.eaters,
    })
  },
  eaters_2: {
    getTracked: (stepName, data) => ({
      ...trackerStepsBase(stepName),
      event_eaters: data.eaters,
    })
  },
  foods: {
    getTracked: (stepName, data) => ({
      ...trackerStepsBase(stepName),
      event_foods: data.foods?.join(", "),
      event_meals: data.meals?.join(", "),
    })
  },
  foodsBusinessOccasion: {
    getTracked: (stepName, data) => ({
      ...trackerStepsBase(stepName),
      event_food_types: [...data.food_types],
    })
  },
  cuisine_preferences: {
    getTracked: (stepName, data) => ({
      ...trackerStepsBase(stepName),
      event_cuisine_preferences: Array.isArray(data.cuisines),
    })
  },
  cuisine_selection: {
    getTracked: (stepName, data) => ({
      ...trackerStepsBase(stepName),
      ...cuisineFields(data)
    })
  },
  contact: {
    getTracked: (stepName, data) => ({
      ...trackerStepsBase(stepName)
    })
  },
  budget: {
    getTracked: (stepName, data) => ({
      ...trackerStepsBase(stepName),
      event_budget: data.budget,
    })
  },
  min_guarantee: {
    getTracked: (stepName, data) => ({
      ...trackerStepsBase(stepName),
      event_min_guarantee: data.min_guarantee,
    })
  },
  timeframe: {
    getTracked: (stepName, data) => ({
      ...trackerStepsBase(stepName),
      event_timeframe: data.timeframe,
    })
  },
  comment: {
    getTracked: (stepName, data) => ({
      ...trackerStepsBase(stepName),
      event_has_comment: hasComments(data),
    })
  },
  success: {
    getTracked: (stepName, data) => ({
      ...trackerStepsBase(stepName),
      event_address_city: data.address?.city_name,
      event_address_region: data.address?.region_code,
      event_address_country: data.address?.country_code,
      event_budget: data.budget,
      event_eaters: data.eaters,
      event_foods: data.foods?.join(", "),
      ...cuisineFields(data),
      event_meals: data.meals?.join(", "),
      event_has_comment: hasComments(data),
      event_location_status: data.location_status,
      event_occasion: data.occasion?.name,
      event_payer: data.payer,
      event_payer_label: leadType(data.payer),
      event_timeframe: data.timeframe,
      event_id: data.id
    })
  },
  fail: {
    getTracked: (stepName, data) => ({
      ...trackerStepsBase(stepName),
      event_address_city: data.address?.city_name,
      event_address_region: data.address?.region_code,
      event_address_country: data.address?.country_code,
      event_budget: data.budget,
      event_eaters: data.eaters,
      event_foods: data.foods?.join(", "),
      ...cuisineFields(data),
      event_meals: data.meals?.join(", "),
      event_location_status: data.location_status,
      event_occasion: data.occasion?.name,
      event_payer: data.payer,
      event_payer_label: leadType(data.payer),
      event_timeframe: data.timeframe,
    })
  },
}